import * as React from "react";
import Map, { Source, Layer, NavigationControl } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import * as turf from "@turf/turf";

/** Your polygon (as provided) */
const OLYMPIC_PARK_BOUNDS_POLY = {
  type: "Feature",
  properties: {},
  geometry: {
    type: "Polygon",
    coordinates: [[
      [151.06755659584474, -33.84689324308241],
      [151.06786074923298, -33.84748923842382],
      [151.07095455948382, -33.84634855496207],
      [151.0706551584929,  -33.84574860460813],
      [151.06755659584474, -33.84689324308241],
    ]],
  },
};

/**
 * Feathering by drawing rings around the polygon.
 * Mapbox fill has no blur, so this is the standard “manual feather” technique.
 */
function createFeatherRings(polygonFeature, steps = 8, stepMeters = 18) {
  const rings = [];
  for (let i = 1; i <= steps; i++) {
    const outer = turf.buffer(polygonFeature, i * stepMeters, { units: "meters" });
    const inner = turf.buffer(polygonFeature, (i - 1) * stepMeters, { units: "meters" });
    const ring = turf.difference(turf.featureCollection([outer, inner]));
    if (!ring) continue;

    rings.push({
      idx: i,
      feature: ring,
      opacity: 0.24 / Math.pow(i, 1.25), // fades out as it expands
    });
  }
  return rings;
}

export default function PortraitOlympicParkMap() {
  const mapRef = React.useRef(null);
  const containerRef = React.useRef(null);

  // Clean polygon (fixes minor ring/validity issues)
  const poly = React.useMemo(
    () => turf.buffer(OLYMPIC_PARK_BOUNDS_POLY, 0, { units: "meters" }),
    []
  );

  const bbox = React.useMemo(() => turf.bbox(poly), [poly]);
  const sw = React.useMemo(() => [bbox[0], bbox[1]], [bbox]);
  const ne = React.useMemo(() => [bbox[2], bbox[3]], [bbox]);

  const centroid = React.useMemo(() => turf.centroid(poly).geometry.coordinates, [poly]);

  const featherRings = React.useMemo(() => createFeatherRings(poly, 10, 14), [poly]);

  const applyFitBounds = React.useCallback(() => {
    const map = mapRef.current?.getMap?.();
    const el = containerRef.current;
    if (!map || !el) return;

    const { width, height } = el.getBoundingClientRect();

    // Portrait-friendly pixel padding:
    // - more vertical padding to keep it nicely framed in a tall viewport
    const padX = Math.round(width * 0.10);
    const padTop = Math.round(height * 0.14);
    const padBottom = Math.round(height * 0.12);

    map.fitBounds([sw, ne], {
      padding: { top: padTop, right: padX, bottom: padBottom, left: padX },
      duration: 0,
      bearing: 66,
      // pitch: 45,
      maxZoom: 18,
    });
  }, [sw, ne]);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const ro = new ResizeObserver(() => requestAnimationFrame(applyFitBounds));
    ro.observe(el);
    return () => ro.disconnect();
  }, [applyFitBounds]);

  return (
    // Portrait container: responsive but always portrait-ish.
    // - width clamps to a sensible max
    // - aspectRatio forces portrait shape
    // - height caps to viewport so it doesn’t overflow the page
    <div
      ref={containerRef}
      style={{
        width: "100%",
        aspectRatio: "9 / 16",
        maxHeight: "92vh",
        margin: "0 auto",
        borderRadius: 16,
        overflow: "hidden",
        background: "#fff",
      }}
    >
      <Map
        ref={mapRef}
        mapboxAccessToken={"pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw"}
        mapStyle="mapbox://styles/mapbox/streets-v12"
        initialViewState={{
          longitude: centroid[0],
          latitude: centroid[1],
          zoom: 16,
          bearing: 25,
          pitch: 45,
        }}
        onLoad={applyFitBounds}
        dragRotate
        touchPitch
        scrollZoom
        doubleClickZoom
        touchZoomRotate
        cooperativeGestures={false}
      >
        <NavigationControl position="top-right" />

        {/* Faded edges (no outline) */}
        {featherRings.map((r) => (
          <Source key={r.idx} id={`feather-${r.idx}`} type="geojson" data={r.feature}>
            <Layer
              id={`feather-fill-${r.idx}`}
              type="fill"
              paint={{
                "fill-color": "#ffffff",
                "fill-opacity": r.opacity,
                "fill-antialias": true,
              }}
            />
          </Source>
        ))}

        {/* Interior fill: set to >0 if you want the polygon slightly highlighted */}
        <Source id="focus-poly" type="geojson" data={poly}>
          <Layer
            id="focus-fill"
            type="fill"
            paint={{
              "fill-opacity": 0.0, // try 0.08 if you want a subtle highlight
              "fill-antialias": true,
            }}
          />
        </Source>
      </Map>
    </div>
  );
}
